// Пайплайн (04 §6) — dispatch, watch, drill into what failed.
//
// Two honesty rules run through this screen:
//
//   · A stage that is not implemented yet is shown, greyed, labelled — never silently dropped
//     from a preset. The run ledger records it as `skipped_unimplemented` for the same reason:
//     a preset that quietly does five of seven things is a preset that lies.
//   · A failed run is not a red dot. `pipeline_items` holds one row per item per stage, so the
//     question "what actually failed" has an answer, and this screen is where it is asked.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { STAGES, type Stage } from '@leadg/shared';
import {
  isImplemented,
  PRESETS,
  useBacklog,
  useCancelRun,
  useDispatchRun,
  useFailedItems,
  useRuns,
  useStageRuns,
} from './usePipeline.js';
import './pipeline.css';

const ACTIVE = new Set(['queued', 'running', 'cancel_requested']);

const STAGE_TITLES: Record<string, string> = {
  discovery: 'Търсене на обекти',
  details: 'Обогатяване (Places, OSM)',
  harvest: 'Телефони от директории',
  audit: 'Одит на уебсайт',
  identity: 'Фирма по Търговския регистър',
  resolve: 'Свързване организация ↔ лице',
  score: 'Оценяване и сегментиране',
  documents: 'Досие, оферта, картонче',
  client_audit: 'Одит на клиентски сайтове',
  digests: 'Седмични и месечни обобщения',
};

const PRESET_LABELS: Record<string, string> = {
  full: 'Пълен цикъл',
  existing: 'Само съществуващи обекти',
  resolve: 'Идентичност и документи',
};

const PRESET_DESCS: Record<string, string> = {
  full: 'Откриване, обогатяване, одит, регистър, оценка и досиета',
  existing: 'Преизчисляване и одит без ново търсене в Google',
  resolve: 'Само свързване с регистъра, оценка и документи',
};

function StatePill({ status }: { status: string }) {
  const isPulse = status === 'running' || status === 'queued';
  return (
    <span className={`pipe__pill pipe__pill--${status}`}>
      {isPulse ? <span className="pipe__pill-pulse" /> : null}
      {status}
    </span>
  );
}

export function PipelinePage() {
  const [selected, setSelected] = useState<Set<Stage>>(new Set(PRESETS['full']));
  const [openRun, setOpenRun] = useState<number | null>(null);

  const { data: runs = [], isLoading: runsLoading } = useRuns();
  const { data: backlog } = useBacklog();
  const { data: stageRuns = [] } = useStageRuns(openRun);
  const { data: failed = [] } = useFailedItems(openRun);
  const dispatch = useDispatchRun();
  const cancel = useCancelRun();

  const toggle = (stage: Stage): void => {
    const next = new Set(selected);
    if (next.has(stage)) next.delete(stage);
    else next.add(stage);
    setSelected(next);
  };

  const chosen = STAGES.filter((stage) => selected.has(stage));

  return (
    <div className="pipe">
      {/* Left Column: Simplified Launch Cockpit */}
      <section className="pipe__panel">
        <div className="pipe__panel-head">
          <div>
            <h2 className="pipe__h">Пускане на пайплайн</h2>
            <p className="pipe__panel-sub">
              Изберете готов сценарий или отбележете конкретни етапи за изпълнение
            </p>
          </div>
          <span className="badge badge--info">{chosen.length} избрани</span>
        </div>

        {/* Step 1: Presets + Primary Launch Action */}
        <div className="pipe__presets-box">
          <span className="pipe__presets-title">1. Готови профили (бърз избор)</span>
          <div className="pipe__presets">
            {Object.entries(PRESETS).map(([name, stages]) => {
              const isMatch =
                stages.length === selected.size && stages.every((s) => selected.has(s));
              return (
                <button
                  key={name}
                  type="button"
                  className={`pipe__preset-btn ${isMatch ? 'pipe__preset-btn--active' : ''}`}
                  onClick={() => setSelected(new Set(stages))}
                  title={PRESET_DESCS[name]}
                >
                  {PRESET_LABELS[name] ?? name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Primary Dispatch Button right near presets so it's always above the fold */}
        <div className="pipe__actions">
          <button
            className="pipe__go"
            type="button"
            disabled={chosen.length === 0 || dispatch.isPending}
            onClick={() => dispatch.mutate({ stages: chosen })}
          >
            {dispatch.isPending ? 'Записване на заявка…' : `▶ Пусни ${chosen.length} етапа →`}
          </button>
          {dispatch.isError ? (
            <p className="pipe__err">{(dispatch.error as Error).message}</p>
          ) : null}
          {dispatch.isSuccess ? (
            <p className="pipe__ok">
              ✓ Заявката е записана (#{dispatch.data}). Стартира до минута.
            </p>
          ) : null}
        </div>

        {/* Step 2: Compact 2-Column Stages Checklist */}
        <div className="pipe__stages-wrap">
          <span className="pipe__presets-title">2. Включени етапи в цикъла</span>
          <ul className="pipe__stages">
            {STAGES.map((stage, index) => {
              const built = isImplemented(stage);
              const isChecked = selected.has(stage);
              const num = String(index + 1).padStart(2, '0');
              return (
                <li
                  key={stage}
                  className={`pipe__stage-card ${!built ? 'pipe__stage-card--soon' : ''} ${
                    isChecked && built ? 'pipe__stage-card--selected' : ''
                  }`}
                >
                  <label className="pipe__stage-label">
                    <input
                      type="checkbox"
                      className="pipe__stage-check"
                      checked={isChecked}
                      disabled={!built}
                      onChange={() => toggle(stage)}
                    />
                    <span className="pipe__stage-num">{num}</span>
                    <div className="pipe__stage-info">
                      <span className="pipe__stage-title">{STAGE_TITLES[stage] ?? stage}</span>
                      <span className="pipe__stage-code">
                        {stage}
                        {!built ? ' · още не е готов' : ''}
                      </span>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Backlog Summary Tiles */}
        {backlog ? (
          <div className="pipe__backlog-box">
            <h3 className="pipe__backlog-title">Текущ беклог в базата</h3>
            <div className="pipe__backlog">
              <div className="pipe__backlog-tile">
                <span className="pipe__backlog-val">{backlog.leads}</span>
                <span className="pipe__backlog-lbl">общо обекта</span>
              </div>
              <div className="pipe__backlog-tile">
                <span className="pipe__backlog-val">{backlog.unaudited}</span>
                <span className="pipe__backlog-lbl">чакат одит</span>
              </div>
              <div className="pipe__backlog-tile">
                <span className="pipe__backlog-val">{backlog.unscored}</span>
                <span className="pipe__backlog-lbl">без оценка</span>
              </div>
              <div className="pipe__backlog-tile">
                <span className="pipe__backlog-val">{backlog.undocumented}</span>
                <span className="pipe__backlog-lbl">без досие</span>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      {/* Right Column: Run Ledger */}
      <section className="pipe__runs">
        <div className="pipe__runs-head">
          <div>
            <h2 className="pipe__h">Журнал на изпълненията</h2>
            <p className="pipe__panel-sub">
              Кликнете върху номер на пускане за детайлен отчет по етапи
            </p>
          </div>
          <span className="badge badge--neutral">{runs.length} записани</span>
        </div>

        {runsLoading ? <p className="pipe__msg">Зареждане на журнал…</p> : null}
        {!runsLoading && runs.length === 0 ? (
          <p className="pipe__msg">Още няма пускания. Стартирайте цикъл от панела вляво.</p>
        ) : null}

        <div className="pipe__runs-list">
          {runs.map((run) => {
            const isOpen = openRun === run.id;
            return (
              <article key={run.id} className={`pipe__run ${isOpen ? 'pipe__run--open' : ''}`}>
                <header className="pipe__runhead">
                  <button
                    type="button"
                    className="pipe__run-id-btn"
                    onClick={() => setOpenRun(isOpen ? null : run.id)}
                  >
                    #{run.id}
                    <span className="pipe__run-expand">{isOpen ? '▾' : '▸'}</span>
                  </button>
                  <StatePill status={run.status} />
                  <span className="pipe__runmeta">
                    <strong>{run.trigger}</strong> · {run.requested_by ?? 'система'} ·{' '}
                    {new Date(run.created_at).toLocaleString('bg-BG')}
                  </span>
                  {ACTIVE.has(run.status) ? (
                    <button
                      type="button"
                      className="pipe__cancel"
                      onClick={() => cancel.mutate(run.id)}
                    >
                      Отказ
                    </button>
                  ) : null}
                </header>

                {run.error ? <p className="pipe__err">{run.error}</p> : null}

                {isOpen ? (
                  <div className="pipe__detail">
                    <table className="pipe__table">
                      <thead>
                        <tr>
                          <th>етап</th>
                          <th>състояние</th>
                          <th>общо</th>
                          <th>готови</th>
                          <th>грешки</th>
                          <th>отложени</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stageRuns.map((stage) => (
                          <tr key={stage.stage}>
                            <td>
                              <span className="pipe__tbl-stage-title">
                                {STAGE_TITLES[stage.stage] ?? stage.stage}
                              </span>
                              <span className="pipe__tbl-stage-code">{stage.stage}</span>
                            </td>
                            <td>
                              <StatePill status={stage.status} />
                            </td>
                            <td className="pipe__num">{stage.items_total ?? 0}</td>
                            <td className="pipe__num pipe__num--ok">{stage.items_done ?? 0}</td>
                            <td className="pipe__num pipe__num--err">{stage.items_failed ?? 0}</td>
                            <td className="pipe__num">{stage.items_deferred ?? 0}</td>
                          </tr>
                        ))}
                        {stageRuns.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="pipe__table-empty">
                              Няма детайли за етапите на това пускане.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>

                    {failed.length > 0 ? (
                      <div className="pipe__failures">
                        <h3 className="pipe__h3">Проблемни елементи ({failed.length})</h3>
                        <ul className="pipe__items">
                          {failed.map((item) => (
                            <li
                              key={`${item.stage}-${item.item_id}`}
                              className="pipe__failure-item"
                            >
                              <Link to={`/leads/${item.item_id}`} className="pipe__item-link">
                                #{item.item_id}
                              </Link>
                              <span className="pipe__item-stage">{item.stage}</span>
                              <span className="pipe__item-status">{item.status}</span>
                              {item.error_class ? (
                                <span className="pipe__item-class">{item.error_class}</span>
                              ) : null}
                              {item.error_msg ? (
                                <span className="pipe__item-msg">{item.error_msg}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
